import React, { useState, useEffect } from "react";
import Loader from "../../../components/common/loader";
import HTMLContent from "../../../components/common/html-content";
import ImgManaged from "../../../components/common/img-managed";
import { Button, Col, Input, Row, InputGroup } from "reactstrap";
import { Link } from "react-router";
import { uniqBy, getDownloadUrl } from "../../../common/util";
import { CHAPEL_LIBRARY } from "../../../common/constants";
import LanguageAvailable from "./language-available";

import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import "../styles/style.css";
import BookDownloadSection from "./book-download-section";
import BookArticles from "./book-articles";
import { Helmet } from "react-helmet";
import BookTopicList from "./book-topic-list";
import AuthorHighlight from "../../common/author-highlight";
import { Flags } from "../../../common/flags";

const Book = (props) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [cartBook, setCartBook] = useState({});
  const { id } = useParams();

  const book = props.currentBook;

  const addBook = (book) => {
    props.addBookToCart(book);
  };

  const removeBook = (book, all = true) => {
    props.removeBookFromCart(book, all);
  };

  const changeQty = (event, book) => {
    let count = event.target.value || 0;
    let _count = parseInt(count, 10);
    const newBook = {
      code: book.code,
      title: book.title,
      type: book.type,
      coverUrl: book.coverUrl,
      price: book.price,
      counter: _count,
    };
    props.changeCartQty(newBook);
  };

  useEffect(() => {
    props.getInfo(id).then((data) => {
      props.changeSelectedBook(data);
      setIsLoading(false);
    });
  }, [id]);

  useEffect(() => {
    let b = props.cartBook(id) ? props.cartBook(id) : {};
    setCartBook(b);
  }, [props.cartBook(id)]);

  function modalToggleAudio(book) {
    let url = process.env.REACT_APP_API_URL + book.audioUrl;
    props.playAudio({
      url,
      title: book.title,
      author: uniqBy(book.authors, JSON.stringify)
        .map((a) => a.name)
        .join(", "),
    });
  }

  function modalToggleAudioArticle(article) {
    let url = process.env.REACT_APP_API_URL + article.audioUrl;
    props.playAudio({
      url,
      title: article.title,
      author: book.number
        ? article.author?.name || "Unknown Author"
        : uniqBy(book.authors, JSON.stringify)
            .map((a) => a.name)
            .join(", "),
    });
  }

  function getArticleAuthor(article) {
    if (typeof article.author === 'string') return article.author;
    if (article.author?.name) return article.author.name;
    return uniqBy(book.authors, JSON.stringify)
      .map((a) => a.name)
      .join(", ") || "Unknown Author";
  }

  function handleAddToQueue(article) {
    const url = process.env.REACT_APP_API_URL + (article.audioUrl || article.audiourl);
    const authorName = getArticleAuthor(article);

    props.addToQueue({
      url,
      title: article.title,
      author: authorName,
    });
    props.sendMessage(t("add-to-queue"), article.title, "success");
  }

  function handlePlayAll(articles) {
    const tracks = articles.map((article) => ({
      url: process.env.REACT_APP_API_URL + (article.audioUrl || article.audiourl),
      title: article.title,
      author: getArticleAuthor(article),
    }));
    props.playAll(tracks);
  }

  function handleQueueAll(articles) {
    const tracks = articles.map((article) => ({
      url: process.env.REACT_APP_API_URL + (article.audioUrl || article.audiourl),
      title: article.title,
      author: getArticleAuthor(article),
    }));
    props.queueAll(tracks);
    props.sendMessage(t("queue-all"), book.title, "success");
  }

  function modalTogglePrice(e) {
    e.preventDefault();
    let messageHeader = t("price");
    let messageBody = () => {
      return (
        <>
          <p>{t("price-explanation-body", {freeLimit: props.dbUser?.freeLimit})}</p>
        </>
      );
    };
    props.openMessageModal(messageHeader, messageBody());
  }

  function renderAuthors(authors) {
    const uniq = uniqBy(authors || [], JSON.stringify);

    return (
      <>
        {uniq.length > 1 ? t("authors") + ": " : ""}
        {uniq.map((a, i) => {
          return (
            <span key={i}>
              <AuthorHighlight author={a} />
              {i === uniq.length - 1 ? "" : ", "}
            </span>
          );
        })}
      </>
    );
  }

  if (isLoading || !book || !book.code) {
    return <Loader height={850} />;
  }

  const audioArticles = (book.articles || []).filter((a) => !!a.audioUrl || !!a.audiourl);

  function handleMainListenAction(e) {
    e.preventDefault();
    if (book.audioUrl || book.audiourl) {
      modalToggleAudio(book);
    } else if (audioArticles.length > 0) {
      handleQueueAll(audioArticles);
    }
  }

  const mainListenButtonText = (book.audioUrl || book.audiourl)
    ? t("listen-online")
    : `${t("listen")} (${audioArticles.length} ${t(audioArticles.length > 1 ? "articles" : "article").toLowerCase()})`;

  const image = process.env.REACT_APP_API_URL + book.coverUrl;

  const fgbStringText = (function (b) {
    switch (b) {
      case "EN":
        return "Free Grace Broadcaster";
      case "ES":
        return "Portavoz de la Gracia";
      case "FR":
        return "Free Grace Broadcaster";
      default:
        return "Free Grace Broadcaster";
    }
  })(book.isoLangCode);
  const fgbString = !!book.number
    ? fgbStringText + " " + book.number + " - "
    : "";

  const parent = !!book.parent ? (
    <div className="language-available">
      <span className="bold me-2">{t("english-title")}:</span>
      <Link
        to={`/book/${book.parent.code}/${book.parent.friendlyTitle}`}
      >
        <img src={Flags["EN"]} alt={"EN"}></img>
        <span>{book.parent.title}</span>
      </Link>
    </div>
  ) : null;

  const children = book.children && book.children.length > 0 ? LanguageAvailable(book, t) : null;

  return (
    <div className="book container pb-5" key={id}>
      <Helmet>
        <title>{`${book.title} - ${CHAPEL_LIBRARY}`}</title>
        <meta name="description" content="" />
      </Helmet>

      <Row className="gx-5">
        <Col lg="3" md="4" className="book-sidebar">
          <div className="image-container">
            <ImgManaged
              src={image}
              alt={book.title}
              type={book.type}
              width={300}
            />
          </div>
        </Col>

        <Col lg="9" md="8" className="book-main-content mt-4 mt-md-0">
          <div className="book-header">
            <h1>{fgbString + book.title}</h1>
            {book.authors && book.authors.length > 0 && (
              <div className="authors">
                {t("by")} {renderAuthors(book.authors)}
              </div>
            )}
          </div>

          <div className="primary-actions">
            <Link
              to={`/read/${book.code}`}
              className="btn btn-outline-primary"
              style={{ display: book.epubUrl ? "flex" : "none" }}
            >
              <i className="fa fa-desktop" aria-hidden="true"></i>
              <span>{t("read-online")}</span>
            </Link>

            {(book.audioUrl || book.audiourl || audioArticles.length > 0) && (
              <Button
                className="btn btn-outline-primary"
                onClick={handleMainListenAction}
              >
                <i className="fa fas fa-volume-up"></i>
                <span>{mainListenButtonText}</span>
              </Button>
            )}

            {book.hasPrintableVersion && props.canAddToCart && (
              <Button
                onClick={() => addBook(book)}
                className="btn btn-info"
              >
                <i className="fa fas fa-shopping-cart"></i>
                <span>{t("add-to-cart")}</span>
              </Button>
            )}

            {!props.canAddToCart && (
              <div className="cart-management-inline">
                <Button
                  onClick={() => removeBook(book)}
                  className="btn btn-danger"
                  title={t("remove-from-cart")}
                >
                  <i className="fa fas fa-trash"></i>
                </Button>

                {Object.keys(cartBook).length > 0 && (
                  <div className="qty-selector-mini">
                    <InputGroup>
                      <Button color="outline-secondary" onClick={() => removeBook(book, false)}>
                        <i className="fa fas fa-minus"></i>
                      </Button>
                      <Input
                        type="text"
                        name="counter"
                        value={cartBook.counter}
                        onChange={(e) => changeQty(e, book)}
                      />
                      <Button color="outline-secondary" onClick={() => addBook(book)}>
                        <i className="fa fas fa-plus"></i>
                      </Button>
                    </InputGroup>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="description-section">
            <HTMLContent content={book.description} />
          </div>

          <div className="book-specs-grid">
            <div className="spec-item">
              <span className="spec-label">{t("format")}</span>
              <span className="spec-value">{book.type ? t(book.type.toLowerCase()) : ""}</span>
            </div>
            <div className="spec-item">
              <span className="spec-label">{t("pages")}</span>
              <span className="spec-value">{book.pageCount}</span>
            </div>
            <div className="spec-item">
              <span className="spec-label">{t("language")}</span>
              <span className="spec-value">{book.language}</span>
            </div>
            <div className="spec-item">
              <span className="spec-label">{t("code")}</span>
              <span className="spec-value font-monospace">{book.code}</span>
            </div>
            <div className="spec-item">
              <span className="spec-label">{t("value")}</span>
              <span className="spec-value">
                ${(book.price || 0).toFixed(2)}
                <button
                  onClick={(e) => modalTogglePrice(e)}
                  className="btn btn-link p-0 ms-2 text-decoration-none"
                  style={{ verticalAlign: "baseline" }}
                >
                  <i className="fa fa-question-circle text-muted"></i>
                </button>
              </span>
            </div>
          </div>

          <BookDownloadSection
            book={book}
            t={t}
            getDownloadUrl={getDownloadUrl}
          />

          <div className="section-title">{t(book.number ? "articles" : "contents")}</div>
          <BookArticles
            book={book}
            t={t}
            renderAuthors={renderAuthors}
            modalToggleAudioArticle={modalToggleAudioArticle}
            handleAddToQueue={handleAddToQueue}
            handlePlayAll={handlePlayAll}
            handleQueueAll={handleQueueAll}
          />

          {parent}
          {children}

          <div className="section-title">{t("topics")}</div>
          <BookTopicList bookCode={book.code} />
        </Col>
      </Row>
    </div>
  );
};

export default Book;
